import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { SkeletonDetailPage } from "../components/SkeletonLoader";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import FadeIn from "../components/FadeIn";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Download, Upload, X, Loader2, Settings, Edit2, Trash2, CarFront, FileText, Copy, Check, Camera, ClipboardCheck, AlertTriangle, Sparkles, Wand2 } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import Modal from "../components/Modal";
import { MACHINE_TYPES, MACHINE_STATUSES, MACHINE_STATUS_LABELS, MACHINE_STATUS_COLORS, DEFAULT_MACHINE_STATUS } from '../constants';
import AuditLogView from "../components/AuditLogView";
import MachineSiteSelect from "../components/MachineSiteSelect";
import AiSummaryCard from "../components/ai/AiSummaryCard";
import AiDocumentIntake from "../components/ai/AiDocumentIntake";
import AiPdfEditor from "../components/ai/AiPdfEditor";
import { DeliveryDeadlineBadge } from "../components/DeliveryDeadline";
import MachineUpgradesCard from "../components/upgrades/MachineUpgradesCard";

const isPdfName = (name) => /\.pdf$/i.test(name || "");

const ORDER_STATUS_LABELS = { open: "Otevřená", in_progress: "Ve zpracování", completed: "Dokončená", cancelled: "Zrušená" };
const ORDER_STATUS_COLORS = { open: "#FFA726", in_progress: "#42A5F5", completed: "#66BB6A", cancelled: "#EF5350" };

const MachineDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [machine, setMachine] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useDelayedLoading(loading, 250);
  const [generating, setGenerating] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [activeTab, setActiveTab] = useState("dokumenty");
  const [templateVersions, setTemplateVersions] = useState({});
  const [selectedVersions, setSelectedVersions] = useState({});
  const [originalSnapshot, setOriginalSnapshot] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [user, setUser] = useState(null);
  const [copyToast, setCopyToast] = useState(false);
  const [orderToast, setOrderToast] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [savingVersions, setSavingVersions] = useState(false);
  const [versionsSaved, setVersionsSaved] = useState(false);
  const [photos, setPhotos] = useState({ left: [], right: [], front: [], back: [], other: [] });
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [browsePosition, setBrowsePosition] = useState(null);
  const [features, setFeatures] = useState([]);
  const [featuresDirty, setFeaturesDirty] = useState(false);
  const [showAiIntake, setShowAiIntake] = useState(false);
  const [pdfEditSource, setPdfEditSource] = useState(null);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresMsg, setFeaturesMsg] = useState(null);
  const featuresMsgTimerRef = useRef(null);

  const statusLabels = MACHINE_STATUS_LABELS;
  const statusColors = MACHINE_STATUS_COLORS;

  useEffect(() => { fetchData(); fetchUser(); fetchPhotos(); }, [id]);

  // "Odvoz a předání": start a protocol, or open the one already in progress for this machine
  const startDelivery = async () => {
    try {
      const res = await fetch("/api/delivery-protocols", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ machine_id: Number(id) }) });
      const json = await res.json().catch(() => ({}));
      if (res.ok || (res.status === 409 && json.data?.id)) navigate("/odvozy/" + json.data.id);
      else alert(json.error || "Protokol se nepodařilo založit");
    } catch (err) {
      alert("Protokol se nepodařilo založit");
    }
  };

  const fetchPhotos = async () => {
    try {
      const res = await fetch(`/api/machines/${id}/photos`, { credentials: "include" });
      if (res.ok) {
        const json = await res.json();
        setPhotos(json.data);
      }
    } catch (err) {
      console.error("fetchPhotos error:", err);
    }
  };

  const uploadPhoto = async (position, file) => {
    setUploadingPhoto(position);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      const res = await fetch(`/api/machines/${id}/photos/${position}`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      if (res.ok) {
        await fetchPhotos();
      } else {
        const json = await res.json();
        alert(json.error || "Chyba při nahrávání");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUploadingPhoto(null);
    }
  };

  const uploadMultiplePhotos = async (files) => {
    const fileArray = Array.from(files).slice(0, 10);
    setUploadingPhoto("other");
    for (const file of fileArray) {
      const formData = new FormData();
      formData.append("photo", file);
      const res = await fetch(`/api/machines/${id}/photos/other`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        alert(json.error || "Chyba při nahrávání");
        break;
      }
    }
    await fetchPhotos();
    setUploadingPhoto(null);
  };

  const deletePhoto = async (photoId) => {
    try {
      const res = await fetch(`/api/machines/${id}/photos/${photoId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        await fetchPhotos();
      } else {
        const json = await res.json().catch(() => ({}));
        alert(json.error || "Chyba při mazání fotky");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUser = async () => {
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (res.ok) {
        const json = await res.json();
        setUser(json.user || json.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [machineRes, templatesRes, docsRes] = await Promise.all([
        fetch("/api/machines/" + id, { credentials: "include" }),
        fetch("/api/templates", { credentials: "include" }),
        fetch("/api/machines/" + id + "/documents", { credentials: "include" })
      ]);
      if (machineRes.ok) { const json = await machineRes.json(); const m = json.data; setMachine(m); if (m?.template_version_snapshot) try { const snap = typeof m.template_version_snapshot === 'string' ? JSON.parse(m.template_version_snapshot) : m.template_version_snapshot; setOriginalSnapshot(snap); setSelectedVersions(snap); } catch (e) { console.error('Template snapshot parse error:', e); } }
      if (templatesRes.ok) { const json = await templatesRes.json(); const tmpl = json.data || []; setTemplates(tmpl); tmpl.forEach(t => fetchVersionsFor(t.id)); }
      if (docsRes.ok) { const json = await docsRes.json(); setDocuments(json.data || []); }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchVersionsFor = async (tid) => { try { const res = await fetch("/api/templates/" + tid + "/versions", { credentials: "include" }); if (res.ok) { const json = await res.json(); setTemplateVersions(prev => ({ ...prev, [tid]: json.data || [] })); } } catch (e) {} };

  const fetchFeatures = async () => {
    setFeaturesLoading(true);
    try {
      const res = await fetch(`/api/machines/${id}/features`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setFeatures(data.data);
        setFeaturesDirty(false);
      }
    } catch (err) {
      console.error(err);
    }
    setFeaturesLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'konfigurace') fetchFeatures();
  }, [activeTab]);

  const saveFeatures = async () => {
    try {
      const res = await fetch(`/api/machines/${id}/features`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: features.map(f => ({ feature_id: f.id, has_feature: f.has_feature }))
        })
      });
      const data = await res.json();
      if (data.success) {
        setFeaturesDirty(false);
        setFeaturesMsg({ type: 'success', text: 'Konfigurace uložena' });
      } else {
        setFeaturesMsg({ type: 'error', text: data.error || 'Chyba při ukládání' });
      }
    } catch (err) {
      setFeaturesMsg({ type: 'error', text: 'Chyba při ukládání' });
    }
    if (featuresMsgTimerRef.current) clearTimeout(featuresMsgTimerRef.current);
    featuresMsgTimerRef.current = setTimeout(() => setFeaturesMsg(null), 3000);
  };

  const handleCreateOrder = async () => {
    setCreatingOrder(true);
    let toast;
    try {
      const res = await fetch("/api/orders/from-machine/" + id, { method: "POST", credentials: "include" });
      const json = await res.json();
      if (json.success) {
        await fetchData();
        toast = { type: "success", msg: json.created ? "Objednávka dílů vytvořena" : "Napojeno na existující objednávku" };
      } else {
        toast = { type: "error", msg: json.error || "Objednávku se nepodařilo vytvořit" };
      }
    } catch (err) {
      toast = { type: "error", msg: err.message || "Objednávku se nepodařilo vytvořit" };
    }
    setCreatingOrder(false);
    setOrderToast(toast);
    setTimeout(() => setOrderToast(null), 3000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 2000);
    });
  };

  const handleDownload = async (template) => {
    setGenerating(template.id);
    try {
      const body = { machineId: id };
      if (selectedVersions[template.id]) body.useVersion = selectedVersions[template.id];
      const res = await fetch("/api/templates/" + template.id + "/generate", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const cd = res.headers.get("content-disposition");
        const match = cd && cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        a.download = match ? decodeURIComponent(match[1].replace(/['"]/g, "")) : template.name + ".docx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Download failed:", e);
    }
    setGenerating(null);
  };

  const handleUploadDoc = async (e, docType) => { const file = e.target.files[0]; if (!file) return; setUploading(docType); const formData = new FormData(); formData.append("file", file); formData.append("document_type", docType); try { await fetch("/api/machines/" + id + "/documents", { method: "POST", credentials: "include", body: formData }); fetchData(); } catch (e) {} setUploading(null); };
  const handleDeleteDoc = async (docId) => { try { await fetch("/api/machines/" + id + "/documents/" + docId, { method: "DELETE", credentials: "include" }); setConfirmAction(null); fetchData(); } catch (e) {} };
  
  const handleStatusChange = async (status) => {
    if (machine.status === "nahled" && status !== "nahled") {
      setConfirmAction({
        title: "Změna statusu trenažéru",
        message: "Při změně z Náhledu se přidělí skutečné sériové číslo. Tuto akci nelze vrátit. Pokračovat?",
        confirmText: "Pokračovat",
        danger: false,
        onConfirm: async () => {
          setConfirmAction(null);
          try {
            const res = await fetch("/api/machines/" + id, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ status }) });
            if (res.ok) fetchData();
          } catch (e) { console.error(e); }
        }
      });
      return;
    }
    try {
      const res = await fetch("/api/machines/" + id, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ status }) });
      if (res.ok) fetchData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteMachine = () => {
    setConfirmAction({
      title: "Smazat trenažér?",
      message: "Opravdu chcete smazat tento trenažér? Tato akce je nevratná.",
      danger: true,
      onConfirm: async () => {
        try {
          await fetch("/api/machines/" + id, { method: "DELETE", credentials: "include" });
          setConfirmAction(null);
          navigate(-1);
        } catch (e) { console.error(e); }
      }
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (machine.status === "nahled" && editForm.status !== "nahled") {
      setConfirmAction({
        title: "Změna statusu trenažéru",
        message: "Při změně z Náhledu se přidělí skutečné sériové číslo. Tuto akci nelze vrátit. Pokračovat?",
        confirmText: "Pokračovat",
        danger: false,
        onConfirm: async () => {
          setConfirmAction(null);
          try {
            const res = await fetch("/api/machines/" + id, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(editForm) });
            if (res.ok) { setEditing(false); fetchData(); }
          } catch (e) { console.error(e); }
        }
      });
      return;
    }
    try {
      const res = await fetch("/api/machines/" + id, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(editForm) });
      if (res.ok) { setEditing(false); fetchData(); }
    } catch (e) { console.error(e); }
  };

  const openEditModal = () => {
    setEditForm({
      serial_number: machine.serial_number || "",
      status: machine.status || DEFAULT_MACHINE_STATUS,
      contract_signed_at: machine.contract_signed_at ? machine.contract_signed_at.split("T")[0] : "",
      delivered_at: machine.delivered_at ? machine.delivered_at.split("T")[0] : "",
      delivery_deadline: machine.delivery_deadline ? String(machine.delivery_deadline).split("T")[0] : ""
    });
    setEditing(true);
  };

  const getRelevantTemplates = () => {
    if (!machine) return [];
    const mt = machine.model_type;
    return templates.filter(t => {
      const n = (t.name + t.original_name).toLowerCase();
      const is1sk = n.includes("1. sk") || n.includes("(1.sk");
      // Šablony specifické pro 1. skupinu
      if (is1sk && n.includes("tr3")) return mt === "TR3 (1. sk.)";
      if (is1sk && n.includes("tr4")) return mt === "TR4 (1. sk.)";
      // CE TR3 → TR3, TR3 (1. sk.) i TR4 (1. sk.)
      if (n.includes("ce") && n.includes("tr3")) return mt === "TR3" || mt === "TR3 (1. sk.)" || mt === "TR4 (1. sk.)";
      // CE TR4 → pouze TR4
      if (n.includes("ce") && n.includes("tr4")) return mt === "TR4";
      // Běžné OS TR3/TR4 šablony (bez 1. sk.)
      if (n.includes("tr3") && !is1sk) return mt === "TR3";
      if (n.includes("tr4") && !is1sk) return mt === "TR4";
      // Generické šablony → všechny typy
      return !n.includes("tr3") && !n.includes("tr4");
    });
  };
  const handleSaveVersions = async () => {
    setSavingVersions(true);
    try {
      const res = await fetch("/api/machines/" + id, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ template_version_snapshot: selectedVersions })
      });
      if (res.ok) {
        setOriginalSnapshot({ ...selectedVersions });
        setVersionsSaved(true);
        setTimeout(() => setVersionsSaved(false), 2500);
        fetchData();
      }
    } catch (e) { console.error(e); }
    setSavingVersions(false);
  };

  const getDocForType = (type) => documents.find(d => d.document_type === type && !d.is_deleted);
  const canUpload = (name) => !name.toLowerCase().includes("manuál") && !name.toLowerCase().includes("manual");
  const docSlotType = (template) => template.name.toLowerCase().replace(/\s+/g, "_");
  // Documents not shown in a template slot (AI intake, edited PDFs, handover protocols, duplicates of a slot type).
  const slotDocIds = new Set(getRelevantTemplates().filter((t) => canUpload(t.name)).map((t) => getDocForType(docSlotType(t))?.id).filter(Boolean));
  const otherDocs = documents.filter((d) => !d.is_deleted && !slotDocIds.has(d.id));
  const downloadDoc = async (doc) => {
    const res = await fetch("/api/machines/" + id + "/documents/" + doc.id + "/download", { credentials: "include" });
    if (!res.ok) return;
    const url = window.URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = doc.original_name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  const openPdfEdit = (doc) => setPdfEditSource({ kind: "machine_doc", machineId: Number(id), docId: doc.id, name: doc.title || doc.original_name });
  const aiIconButton = { display: "flex", alignItems: "center", gap: "4px", padding: "8px", backgroundColor: "#F3E5F5", border: "none", cursor: "pointer", borderRadius: "8px", color: "#7B1FA2", fontSize: "12px" };

  if (showLoading) return <SkeletonDetailPage />;
  if (!machine) return <div style={{ padding: "32px", color: "#666" }}>Trenažér nenalezen</div>;

  const sc = statusColors[machine.status] || statusColors[DEFAULT_MACHINE_STATUS];
  const isPreview = machine.status === "nahled";
  const inputStyle = { width: "100%", padding: "10px 14px", border: "1px solid #E0E0E0", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: "13px", fontWeight: 500, color: "#333", marginBottom: "6px" };
  const canSeeHistory = user && (user.role === 'admin' || user.role === 'manager');

  return (
    <FadeIn show={!showLoading}>
    <div style={{ padding: "24px" }}>
      <button onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: "8px", background: "none", border: "none", color: "#666", cursor: "pointer", marginBottom: "24px", fontSize: "14px" }}>
        <ArrowLeft size={20} /> Zpět
      </button>

      {isPreview && (
        <div style={{ backgroundColor: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "20px" }}>👁️</span>
          <span style={{ color: "#FF8F00", fontWeight: 500 }}>Náhled - sériové číslo je dočasné. Při změně statusu se přidělí skutečné.</span>
        </div>
      )}

      <div style={{ backgroundColor: "#FFF", borderRadius: "12px", border: "1px solid #E0E0E0", padding: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ backgroundColor: isPreview ? "#FF8F00" : "#E53935", padding: "12px", borderRadius: "12px" }}>
              <CarFront size={28} color="#FFF" />
            </div>
            <div>
              <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#333", margin: "0 0 4px" }}>Autom {machine.model_type}</h1>
              {machine.customer_name && <Link to={"/customers/" + machine.customer_id} style={{ color: "#666", textDecoration: "none", fontSize: "14px" }}>{machine.customer_name}</Link>}
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={startDelivery} title="Odvoz a předání" style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 12px", borderRadius: "8px", border: "none", backgroundColor: "#111", color: "#FFF", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}><ClipboardCheck size={16} /> Odvoz a předání</button>
            <button onClick={openEditModal} style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "#F5F5F5", cursor: "pointer" }}><Edit2 size={18} color="#666" /></button>
            <button onClick={handleDeleteMachine} style={{ padding: "8px", borderRadius: "8px", border: "none", backgroundColor: "#FFEBEE", cursor: "pointer" }}><Trash2 size={18} color="#E53935" /></button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Sériové číslo</p>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <p style={{ fontSize: "16px", fontWeight: 600, color: isPreview ? "#FF8F00" : "#333", margin: 0 }}>{machine.serial_number}</p>
              <button onClick={() => copyToClipboard(machine.serial_number)} style={{ background: "none", border: "none", cursor: "pointer", padding: "2px" }}>
                <Copy size={14} color="#999" />
              </button>
            </div>
          </div>
          <div><p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Smlouva</p><p style={{ fontSize: "15px", color: "#333", margin: 0 }}>{machine.contract_signed_at ? new Date(machine.contract_signed_at).toLocaleDateString("cs") : "-"}</p></div>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Doručení</p>
            {machine.delivered_at ? (
              <p style={{ fontSize: "15px", color: "#333", margin: 0 }}>{new Date(machine.delivered_at).toLocaleDateString("cs")}</p>
            ) : machine.status === "doruceno" ? (
              <p style={{ fontSize: "15px", color: "#333", margin: 0 }}>Doručeno <button onClick={openEditModal} style={{ background: "none", border: "none", padding: 0, color: "#999", fontSize: "13px", textDecoration: "underline", cursor: "pointer" }}>doplnit datum</button></p>
            ) : (
              <p style={{ fontSize: "15px", color: "#F57C00", margin: 0 }}>Nedoručeno</p>
            )}
          </div>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Doručit do</p>
            {machine.delivery_deadline || !machine.delivered_at ? (
              <DeliveryDeadlineBadge deadline={machine.delivery_deadline} deliveredAt={machine.delivered_at || (machine.status === "doruceno" ? "yes" : null)} />
            ) : (
              <p style={{ fontSize: "15px", color: "#333", margin: 0 }}>-</p>
            )}
          </div>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Status</p>
            <select value={machine.status || DEFAULT_MACHINE_STATUS} onChange={(e) => handleStatusChange(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #E0E0E0", borderRadius: "8px", fontSize: "14px", fontWeight: 500, backgroundColor: sc.bg, color: sc.color, cursor: "pointer" }}>
              {MACHINE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Místo instalace</p>
            <MachineSiteSelect machineId={machine.id} customerId={machine.customer_id} siteId={machine.site_id} />
          </div>
        </div>

        {machine.notes && (
          <div style={{ backgroundColor: "#FAFAFA", border: "1px solid #EEE", borderRadius: "8px", padding: "10px 14px", marginBottom: "20px", fontSize: "13px", color: "#555", whiteSpace: "pre-wrap" }}>
            <span style={{ color: "#999" }}>Poznámka: </span>{machine.notes}
          </div>
        )}

        <div style={{ marginBottom: "20px" }}>
          <AiSummaryCard kind="machine" id={machine.id} />
        </div>

        {!isPreview && (
          <div style={{ border: "1px solid #E0E0E0", borderRadius: "12px", padding: "12px 16px", marginBottom: "20px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 16px" }}>
            <strong style={{ fontSize: "14px", color: "#333" }}>Objednávka dílů</strong>
            {machine.order_batch_id ? (
              <>
                <span style={{ fontSize: "12px", padding: "2px 10px", borderRadius: "12px", fontWeight: 600, background: (ORDER_STATUS_COLORS[machine.order_status] || "#ccc") + "22", color: ORDER_STATUS_COLORS[machine.order_status] || "#666" }}>{ORDER_STATUS_LABELS[machine.order_status] || machine.order_status}</span>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                  <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>{(machine.order_delivered_count || 0) + " / " + (machine.order_item_count || 0) + " dílů dodáno"}</div>
                  <div style={{ height: "6px", borderRadius: "3px", background: "#EEE", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: (machine.order_item_count > 0 ? Math.min(100, Math.round((machine.order_delivered_count || 0) / machine.order_item_count * 100)) : 0) + "%", background: "#66BB6A" }} />
                  </div>
                </div>
                <Link to={"/orders?open=" + machine.order_batch_id} style={{ fontSize: "13px", fontWeight: 600, color: "#1976D2", textDecoration: "none" }}>Otevřít objednávku</Link>
              </>
            ) : (
              <>
                <span style={{ fontSize: "13px", color: "#666", flex: "1 1 180px" }}>Tento trenažér nemá objednávku dílů</span>
                <button onClick={handleCreateOrder} disabled={creatingOrder} style={{ padding: "6px 14px", border: "none", borderRadius: "6px", background: creatingOrder ? "#ccc" : "#E53935", color: "#FFF", cursor: creatingOrder ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>{creatingOrder ? "Vytvářím…" : "Vytvořit objednávku"}</button>
              </>
            )}
          </div>
        )}

        {!isPreview && <MachineUpgradesCard machineId={machine.id} serialNumber={machine.serial_number} />}

        {/* Tabs */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", borderBottom: "1px solid #E0E0E0", marginBottom: "20px", paddingBottom: "12px" }}>
          <button onClick={() => setActiveTab("dokumenty")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", backgroundColor: activeTab === "dokumenty" ? "#E53935" : "#F5F5F5", color: activeTab === "dokumenty" ? "#FFF" : "#666", cursor: "pointer", fontWeight: 500, fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}><FileText size={16} /> Dokumenty</button>
          <button onClick={() => setActiveTab("nastaveni")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", backgroundColor: activeTab === "nastaveni" ? "#E53935" : "#F5F5F5", color: activeTab === "nastaveni" ? "#FFF" : "#666", cursor: "pointer", fontWeight: 500, fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}><Settings size={16} /> Nastavení</button>
          {canSeeHistory && (
            <button onClick={() => setActiveTab("historie")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", backgroundColor: activeTab === "historie" ? "#E53935" : "#F5F5F5", color: activeTab === "historie" ? "#FFF" : "#666", cursor: "pointer", fontWeight: 500, fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}><FileText size={16} /> Historie</button>
          )}
          <button onClick={() => setActiveTab("fotky")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", backgroundColor: activeTab === "fotky" ? "#E53935" : "#F5F5F5", color: activeTab === "fotky" ? "#FFF" : "#666", cursor: "pointer", fontWeight: 500, fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}><Camera size={16} /> Fotky</button>
          <button onClick={() => setActiveTab("konfigurace")} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", backgroundColor: activeTab === "konfigurace" ? "#E53935" : "#F5F5F5", color: activeTab === "konfigurace" ? "#FFF" : "#666", cursor: "pointer", fontWeight: 500, fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}><Settings size={16} /> Konfigurace</button>
        </div>

        {activeTab === "info" && (
          <div>
            <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#333", margin: "0 0 16px" }}>Základní informace</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
              <div style={{ padding: "16px", backgroundColor: "#F9F9F9", borderRadius: "10px" }}>
                <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Typ</p>
                <p style={{ fontSize: "16px", fontWeight: 600, color: "#333", margin: 0 }}>{machine.model_type}</p>
              </div>
              <div style={{ padding: "16px", backgroundColor: "#F9F9F9", borderRadius: "10px" }}>
                <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Zákazník</p>
                {machine.customer_name ? (
                  <Link to={"/customers/" + machine.customer_id} style={{ fontSize: "16px", fontWeight: 600, color: "#1976D2", textDecoration: "none" }}>{machine.customer_name}</Link>
                ) : (
                  <p style={{ fontSize: "16px", color: "#999", margin: 0 }}>-</p>
                )}
              </div>
              <div style={{ padding: "16px", backgroundColor: "#F9F9F9", borderRadius: "10px" }}>
                <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Vytvořeno</p>
                <p style={{ fontSize: "16px", color: "#333", margin: 0 }}>{machine.created_at ? new Date(machine.created_at).toLocaleDateString("cs") : "-"}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "dokumenty" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: "8px" }}>
              <button onClick={() => setShowAiIntake(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", backgroundColor: "#F3E5F5", color: "#7B1FA2", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 500 }}>
                <Sparkles size={15} /> AI příjem dokumentu
              </button>
              <button onClick={() => setPdfEditSource({ kind: "upload", machineId: Number(id) })} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", backgroundColor: "#F5F5F5", color: "#555", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 500 }}>
                <Wand2 size={15} /> Upravit PDF s AI
              </button>
            </div>
            {getRelevantTemplates().map((template) => {
              const docType = template.name.toLowerCase().replace(/\s+/g, "_");
              const uploadedDoc = getDocForType(docType);
              const showUpload = canUpload(template.name);
              return (
                <div key={template.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px", backgroundColor: "#F9F9F9", borderRadius: "10px", flexWrap: "wrap", gap: "12px" }}>
                  <span style={{ fontWeight: 500, color: "#333" }}>{template.name}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    {showUpload && (uploadedDoc ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {uploadedDoc.missing && <span title="Soubor na serveru chybí – smažte záznam a nahrajte jej znovu" style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#F57C00", fontSize: "13px" }}><AlertTriangle size={15} /> chybí, nahrajte znovu</span>}
                        <button onClick={async () => { const res = await fetch("/api/machines/" + id + "/documents/" + uploadedDoc.id + "/download", { credentials: "include" }); if (res.ok) { const blob = await res.blob(); const url = window.URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = uploadedDoc.original_name; document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url); } }} style={{ background: "none", border: "none", color: "#1976D2", cursor: "pointer", fontSize: "13px" }}>{uploadedDoc.original_name}</button>
                        {isPdfName(uploadedDoc.original_name) && !uploadedDoc.missing && (
                          <button onClick={() => openPdfEdit(uploadedDoc)} title="Upravit PDF pomocí AI" aria-label="Upravit PDF pomocí AI" style={aiIconButton}><Wand2 size={16} /></button>
                        )}
                        <button onClick={() => setConfirmAction({ title: "Smazat dokument?", message: "Dokument bude přesunut do složky Smazané.", danger: true, onConfirm: () => handleDeleteDoc(uploadedDoc.id) })} style={{ padding: "8px", backgroundColor: "#FFEBEE", border: "none", cursor: "pointer", borderRadius: "8px" }}><Trash2 size={16} color="#E53935" /></button>
                      </div>
                    ) : (
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 12px", backgroundColor: "#E3F2FD", borderRadius: "6px", cursor: "pointer", fontSize: "13px", color: "#1976D2", fontWeight: 500 }}>
                        {uploading === docType ? <Loader2 size={14} /> : <Upload size={14} />} Nahrát
                        <input type="file" style={{ display: "none" }} onChange={(e) => handleUploadDoc(e, docType)} />
                      </label>
                    ))}
                    <button onClick={() => handleDownload(template)} disabled={generating === template.id} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 500 }}>
                      {generating === template.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {generating === template.id ? "Stahuji..." : "Stáhnout"}
                    </button>
                  </div>
                </div>
              );
            })}
            {otherDocs.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#333", margin: "0 0 10px" }}>Další dokumenty ({otherDocs.length})</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {otherDocs.map((doc) => (
                    <div key={doc.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "12px 16px", backgroundColor: "#F9F9F9", borderRadius: "10px", flexWrap: "wrap" }}>
                      <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                        <p style={{ margin: 0, fontWeight: 500, color: "#333", overflow: "hidden", textOverflow: "ellipsis" }}>{doc.title || doc.original_name}</p>
                        {doc.description && <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#888" }}>{doc.description}</p>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {doc.missing && <span title="Soubor na serveru chybí" style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#F57C00", fontSize: "13px" }}><AlertTriangle size={15} /> chybí</span>}
                        <button onClick={() => downloadDoc(doc)} style={{ background: "none", border: "none", color: "#1976D2", cursor: "pointer", fontSize: "13px" }}>{doc.original_name}</button>
                        {isPdfName(doc.original_name) && !doc.missing && (
                          <button onClick={() => openPdfEdit(doc)} title="Upravit PDF pomocí AI" aria-label="Upravit PDF pomocí AI" style={aiIconButton}><Wand2 size={16} /></button>
                        )}
                        <button onClick={() => setConfirmAction({ title: "Smazat dokument?", message: "Dokument bude přesunut do složky Smazané.", danger: true, onConfirm: () => handleDeleteDoc(doc.id) })} aria-label="Smazat dokument" style={{ padding: "8px", backgroundColor: "#FFEBEE", border: "none", cursor: "pointer", borderRadius: "8px" }}><Trash2 size={16} color="#E53935" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "nastaveni" && (
          <div>
            <div style={{ marginBottom: "32px", paddingBottom: "24px", borderBottom: "1px solid #E0E0E0" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#333", margin: "0 0 16px" }}>Metadata trenažéru</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
                <div style={{ padding: "16px", backgroundColor: "#F9F9F9", borderRadius: "10px" }}>
                  <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Datum vytvoření</p>
                  <p style={{ fontSize: "15px", fontWeight: 500, color: "#333", margin: 0 }}>
                    {machine.created_at ? new Date(machine.created_at).toLocaleString("cs-CZ", { 
                      year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" 
                    }) : "-"}
                  </p>
                </div>
                <div style={{ padding: "16px", backgroundColor: "#F9F9F9", borderRadius: "10px" }}>
                  <p style={{ fontSize: "13px", color: "#999", margin: "0 0 4px" }}>Datum poslední úpravy</p>
                  <p style={{ fontSize: "15px", fontWeight: 500, color: "#333", margin: 0 }}>
                    {machine.updated_at ? new Date(machine.updated_at).toLocaleString("cs-CZ", { 
                      year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" 
                    }) : "-"}
                  </p>
                </div>
              </div>
            </div>
            
            <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#333", margin: "0 0 16px" }}>Použité šablony pro tento trenažér</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {getRelevantTemplates().map((template) => {
                const versions = templateVersions[template.id] || [];
                const currentVer = selectedVersions[template.id] || originalSnapshot[template.id] || 1;
                return (
                  <div key={template.id} style={{ padding: "16px", backgroundColor: "#F9F9F9", borderRadius: "10px" }}>
                    <p style={{ fontWeight: 500, color: "#333", margin: "0 0 8px" }}>{template.name}</p>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "14px" }}>
                      <span style={{ color: "#999" }}>Při tvoření: v{originalSnapshot[template.id] || 1}</span>
                      <span style={{ color: "#CCC" }}>|</span>
                      <span style={{ color: "#999" }}>Použitá verze:</span>
                      <select value={currentVer} onChange={(e) => setSelectedVersions({ ...selectedVersions, [template.id]: parseInt(e.target.value) })} style={{ padding: "6px 10px", border: "1px solid #E0E0E0", borderRadius: "6px", fontSize: "13px" }}>
                        {versions.length > 0 ? versions.map((v) => <option key={v.version} value={v.version}>v{v.version}{v.is_current ? " (aktuální)" : ""}</option>) : <option value={currentVer}>v{currentVer}</option>}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: "20px" }}>
              <button
                onClick={handleSaveVersions}
                disabled={savingVersions}
                style={{
                  padding: "10px 24px",
                  backgroundColor: versionsSaved ? "#388E3C" : "#E53935",
                  color: "#FFF", border: "none", borderRadius: "8px",
                  cursor: savingVersions ? "not-allowed" : "pointer",
                  fontSize: "14px", fontWeight: 600,
                  display: "flex", alignItems: "center", gap: "8px",
                  transition: "background-color 0.2s"
                }}
              >
                {savingVersions ? <Loader2 size={16} /> : versionsSaved ? <Check size={16} /> : null}
                {versionsSaved ? "Uloženo" : savingVersions ? "Ukládám..." : "Uložit použité verze"}
              </button>
            </div>
          </div>
        )}

        {activeTab === "historie" && canSeeHistory && (
          <div>
            <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#333", margin: "0 0 16px" }}>Historie změn</h3>
            <AuditLogView entityType="machine" entityId={id} />
          </div>
        )}

        {activeTab === "fotky" && (
          <div style={{ padding: "24px 0" }}>
            {/* Grid 2x2 */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
              {[
                { key: "left",  label: "Zleva" },
                { key: "right", label: "Zprava" },
                { key: "front", label: "Zepředu" },
                { key: "back",  label: "Zezadu" }
              ].map(({ key, label }) => {
                const preview = photos[key]?.[0];
                const count = photos[key]?.length || 0;
                return (
                  <div key={key} style={{ border: "1px solid #E0E0E0", borderRadius: "12px", overflow: "hidden", backgroundColor: "#FAFAFA" }}>
                    {/* Preview image */}
                    <div
                      onClick={() => preview && setPreviewPhoto({ url: `/api/machines/${id}/photos/${preview.id}/download`, name: preview.original_name })}
                      style={{ width: "100%", aspectRatio: "4/3", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", cursor: preview ? "pointer" : "default", overflow: "hidden" }}
                    >
                      {preview ? (
                        <img
                          src={`/api/machines/${id}/photos/${preview.id}/download`}
                          alt={preview.original_name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div style={{ textAlign: "center", color: "#BDBDBD" }}>
                          <Camera size={32} />
                          <div style={{ fontSize: "12px", marginTop: "8px" }}>Žádná fotka</div>
                        </div>
                      )}
                    </div>
                    {/* Počet fotek */}
                    {preview && count > 1 && (
                      <div style={{ padding: "8px 12px 0", display: "flex", justifyContent: "flex-end" }}>
                        <span style={{ fontSize: "11px", color: "#999" }}>📷 {count}</span>
                      </div>
                    )}
                    {/* Footer: tlačítka + datum */}
                    <div style={{ padding: "8px 12px 12px", display: "flex", alignItems: "center", gap: "6px" }}>
                      <button
                        onClick={() => setBrowsePosition(key)}
                        style={{ display: "flex", alignItems: "center", gap: "4px", padding: "5px 10px", borderRadius: "6px", border: "1px solid #E0E0E0", backgroundColor: "#FFF", color: "#333", cursor: "pointer", fontSize: "12px" }}
                      >
                        <Camera size={13} /> Procházet
                      </button>
                      {preview && (
                        <a
                          href={`/api/machines/${id}/photos/${preview.id}/download`}
                          download={preview.original_name}
                          style={{ display: "flex", alignItems: "center", gap: "4px", padding: "5px 10px", borderRadius: "6px", backgroundColor: "#F5F5F5", color: "#333", textDecoration: "none", fontSize: "12px" }}
                        >
                          <Download size={13} /> Stáhnout
                        </a>
                      )}
                      <label style={{ display: "flex", alignItems: "center", gap: "4px", padding: "5px 10px", borderRadius: "6px", backgroundColor: "#E53935", color: "#FFF", cursor: "pointer", fontSize: "12px" }}>
                        {uploadingPhoto === key ? <Loader2 size={13} /> : <Upload size={13} />}
                        Nahrát
                        <input type="file" accept=".jpg,.jpeg,.png" style={{ display: "none" }} onChange={(e) => { if (e.target.files[0]) uploadPhoto(key, e.target.files[0]); e.target.value = ""; }} />
                      </label>
                      {preview && (
                        <span style={{ marginLeft: "auto", fontSize: "11px", color: "#999", flexShrink: 0 }}>
                          {new Date(preview.created_at).toLocaleDateString("cs-CZ")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Ostatní fotky */}
            <div style={{ border: "1px solid #E0E0E0", borderRadius: "12px", padding: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                <div style={{ fontWeight: 600, fontSize: "15px", color: "#333" }}>Ostatní fotky</div>
                {photos.other.length > 0 && <span style={{ fontSize: "12px", color: "#999" }}>{photos.other.length}/10</span>}
              </div>
              {photos.other.length < 10 ? (
                <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 16px", borderRadius: "8px", backgroundColor: "#E53935", color: "#FFF", cursor: "pointer", fontSize: "14px", marginBottom: "12px" }}>
                  {uploadingPhoto === "other" ? <Loader2 size={14} /> : <Upload size={14} />}
                  Nahrát fotky
                  <input type="file" accept=".jpg,.jpeg,.png" multiple style={{ display: "none" }} onChange={(e) => { if (e.target.files?.length) uploadMultiplePhotos(e.target.files); e.target.value = ""; }} />
                </label>
              ) : (
                <div style={{ fontSize: "13px", color: "#FF8F00", marginBottom: "12px" }}>Dosažen limit 10 fotek.</div>
              )}
              {photos.other.length === 0 ? (
                <div style={{ color: "#BDBDBD", fontSize: "14px" }}>Žádné ostatní fotky</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {photos.other.map((photo) => (
                    <div key={photo.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px 12px", borderRadius: "8px", backgroundColor: "#F5F5F5" }}>
                      <Camera size={16} style={{ color: "#999", flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: "14px", color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{photo.original_name}</span>
                      <span style={{ fontSize: "12px", color: "#999", flexShrink: 0 }}>
                        {new Date(photo.created_at).toLocaleDateString("cs-CZ")}
                      </span>
                      <a href={`/api/machines/${id}/photos/${photo.id}/download`} download={photo.original_name} aria-label={`Stáhnout ${photo.original_name}`} style={{ color: "#555", display: "flex" }}>
                        <Download size={16} />
                      </a>
                      {(user?.role === "admin" || user?.role === "manager") && (
                        <button
                          onClick={() => setConfirmAction({ type: "deletePhoto", title: "Smazat fotku?", message: `Opravdu chcete smazat fotku "${photo.original_name}"? Tato akce je nevratná.`, danger: true, onConfirm: async () => { setConfirmAction(null); await deletePhoto(photo.id); } })}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#E53935", display: "flex", padding: 0 }}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fullscreen preview modal */}
            {previewPhoto && createPortal(
              <div
                onClick={() => setPreviewPhoto(null)}
                style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000, padding: "24px" }}
              >
                <button onClick={(e) => { e.stopPropagation(); setPreviewPhoto(null); }} style={{ position: "absolute", top: "16px", right: "16px", background: "none", border: "none", color: "#FFF", cursor: "pointer" }}>
                  <X size={28} />
                </button>
                <img
                  src={previewPhoto.url}
                  alt={previewPhoto.name}
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: "8px", objectFit: "contain" }}
                />
              </div>,
              document.body
            )}

            {/* Procházet modal */}
            {browsePosition && createPortal(
              <div
                onClick={() => setBrowsePosition(null)}
                style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000, padding: "24px" }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ backgroundColor: "#FFF", borderRadius: "16px", width: "100%", maxWidth: "540px", maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #E0E0E0" }}>
                    <span style={{ fontWeight: 600, fontSize: "16px" }}>
                      {{ left: "Zleva", right: "Zprava", front: "Zepředu", back: "Zezadu" }[browsePosition]}
                      {" — "}{photos[browsePosition]?.length || 0} {photos[browsePosition]?.length === 1 ? "fotka" : photos[browsePosition]?.length >= 2 && photos[browsePosition]?.length <= 4 ? "fotky" : "fotek"}
                    </span>
                    <button onClick={() => setBrowsePosition(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#666" }}><X size={20} /></button>
                  </div>
                  <div style={{ overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    {(photos[browsePosition] || []).length === 0 ? (
                      <div style={{ color: "#BDBDBD", textAlign: "center", padding: "32px 0" }}>Žádné fotky</div>
                    ) : (photos[browsePosition] || []).map((photo) => (
                      <div key={photo.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px", borderRadius: "10px", border: "1px solid #E0E0E0", backgroundColor: "#FAFAFA" }}>
                        <img
                          src={`/api/machines/${id}/photos/${photo.id}/download`}
                          alt={photo.original_name}
                          style={{ width: "56px", height: "56px", objectFit: "cover", borderRadius: "6px", flexShrink: 0, cursor: "pointer" }}
                          onClick={() => setPreviewPhoto({ url: `/api/machines/${id}/photos/${photo.id}/download`, name: photo.original_name })}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <div style={{ fontSize: "13px", color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{photo.original_name}</div>
                          <div style={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>{new Date(photo.created_at).toLocaleString("cs-CZ")}</div>
                        </div>
                        <button
                          onClick={() => setPreviewPhoto({ url: `/api/machines/${id}/photos/${photo.id}/download`, name: photo.original_name })}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#555", display: "flex", padding: "4px" }}
                          title="Zobrazit"
                        >
                          <Download size={16} />
                        </button>
                        {(user?.role === "admin" || user?.role === "manager") && (
                          <button
                            onClick={() => setConfirmAction({ title: "Smazat fotku?", message: `Opravdu chcete smazat fotku "${photo.original_name}"?`, danger: true, onConfirm: async () => { setConfirmAction(null); await deletePhoto(photo.id); } })}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#E53935", display: "flex", padding: "4px" }}
                            title="Smazat"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>,
              document.body
            )}
          </div>
        )}

        {activeTab === 'konfigurace' && (
          <div>
            {featuresMsg && (
              <div style={{
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                backgroundColor: featuresMsg.type === 'success' ? '#E8F5E9' : '#FFEBEE',
                color: featuresMsg.type === 'success' ? '#388E3C' : '#E53935',
                fontSize: '14px'
              }}>
                {featuresMsg.text}
              </div>
            )}

            {featuresLoading ? (
              <div style={{ color: '#999', fontSize: '14px' }}>Načítání...</div>
            ) : features.length === 0 ? (
              <div style={{ color: '#999', fontSize: '14px' }}>
                Žádné technologie nejsou definovány. Přidejte je v Administraci.
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {features.map(feature => (
                    <label
                      key={feature.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #E0E0E0',
                        backgroundColor: feature.has_feature ? '#E8F5E9' : '#FAFAFA',
                        userSelect: 'none'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={feature.has_feature}
                        onChange={() => {
                          setFeatures(prev => prev.map(f =>
                            f.id === feature.id ? { ...f, has_feature: !f.has_feature } : f
                          ));
                          setFeaturesDirty(true);
                        }}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      {feature.name}
                    </label>
                  ))}
                </div>

                <button
                  onClick={saveFeatures}
                  disabled={!featuresDirty}
                  style={{
                    padding: '10px 24px',
                    backgroundColor: featuresDirty ? '#E53935' : '#CCC',
                    color: '#FFF',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: featuresDirty ? 'pointer' : 'not-allowed',
                    fontWeight: 600,
                    fontSize: '14px'
                  }}
                >
                  Uložit
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal show={!!confirmAction} title={confirmAction?.title} message={confirmAction?.message} confirmText={confirmAction?.confirmText || "Smazat"} cancelText="Zrušit" danger={confirmAction?.danger} onConfirm={confirmAction?.onConfirm} onCancel={() => setConfirmAction(null)} />

      <Modal show={editing} onClose={() => setEditing(false)} zIndex={9999}>
          <div style={{ backgroundColor: "#FFF", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "400px", boxShadow: "0 20px 25px rgba(0,0,0,0.15)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}>Upravit trenažér</h2>
              <button onClick={() => setEditing(false)} style={{ padding: "4px", background: "none", border: "none", cursor: "pointer" }}><X size={20} /></button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Sériové číslo</label>
                <input type="text" value={editForm.serial_number} onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })} style={inputStyle} disabled={isPreview} />
                {isPreview && <p style={{ fontSize: "12px", color: "#FF8F00", margin: "4px 0 0" }}>Číslo se změní automaticky při změně statusu</p>}
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Status</label>
                <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} style={inputStyle}>
                  {MACHINE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Datum podpisu smlouvy</label>
                <input type="date" value={editForm.contract_signed_at} onChange={(e) => setEditForm({ ...editForm, contract_signed_at: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label style={labelStyle}>Datum doručení</label>
                <input type="date" value={editForm.delivered_at} onChange={(e) => setEditForm({ ...editForm, delivered_at: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ marginBottom: "20px" }}>
                <label style={labelStyle}>Doručit do (smluvní termín)</label>
                <input type="date" value={editForm.delivery_deadline} onChange={(e) => setEditForm({ ...editForm, delivery_deadline: e.target.value })} style={inputStyle} />
                <p style={{ fontSize: "12px", color: "#999", margin: "4px 0 0" }}>Výchozí: 2 měsíce od podpisu smlouvy.</p>
              </div>
              <div style={{ display: "flex", gap: "12px" }}>
                <button type="button" onClick={() => setEditing(false)} style={{ flex: 1, padding: "12px", border: "1px solid #E0E0E0", borderRadius: "8px", backgroundColor: "#FFF", cursor: "pointer", fontSize: "14px" }}>Zrušit</button>
                <button type="submit" style={{ flex: 1, padding: "12px", backgroundColor: "#E53935", color: "#FFF", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>Uložit</button>
              </div>
            </form>
          </div>
      </Modal>

      <AiDocumentIntake
        open={showAiIntake}
        onClose={() => setShowAiIntake(false)}
        presetCustomerId={machine.customer_id || undefined}
        presetMachineId={machine.id}
        onSaved={() => fetchData()}
      />
      <AiPdfEditor
        open={Boolean(pdfEditSource)}
        source={pdfEditSource}
        onClose={() => setPdfEditSource(null)}
        onSaved={() => fetchData()}
      />

      {orderToast && (
        <div onClick={() => setOrderToast(null)} style={{ position: "fixed", bottom: "20px", right: "20px", background: orderToast.type === "error" ? "#F44336" : "#4CAF50", color: "#FFF", padding: "12px 20px", borderRadius: "8px", zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.15)", cursor: "pointer", maxWidth: "400px" }}>
          {orderToast.msg}
        </div>
      )}

      {/* Copy Toast */}
      {copyToast && (
        <div style={{ position: "fixed", bottom: "20px", right: "20px", background: "#4CAF50", color: "#FFF", padding: "12px 20px", borderRadius: "8px", zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
          ✓ Zkopírováno
        </div>
      )}
    </div>
    </FadeIn>
  );
};

export default MachineDetail;
